import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { User, LogIn, LogOut, Building2, Database, UserPlus, QrCode, DoorOpen, FileText, BarChart3, ArrowRight, ScanLine } from "lucide-react";
import { fullName, qrValue } from "@/lib/demo/utils";
import { useI18n } from "@/lib/i18n/i18n";

const Connector = () => (
  <div className="relative mx-auto h-9 w-px bg-gradient-to-b from-slate-300 to-upv/60"><span className="flow-dot" /></div>
);

const Node = ({ icon: Icon, label, sub, testId }) => {
  const { t } = useI18n();
  return (
  <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-4 text-center shadow-sm transition-transform duration-200 hover:-translate-y-0.5" data-testid={testId}>
    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-upv-light text-upv"><Icon className="h-4 w-4" /></span>
    <span className="font-display text-sm font-bold tracking-wide text-slate-900">{t(label)}</span>
    {sub && <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-upv">{t(sub)}</span>}
  </div>
  );
};

export const ConceptDiagram = ({ participant }) => {
  const { t } = useI18n();
  return (
  <div className="dot-grid relative rounded-[32px] border border-slate-200 bg-slate-50/80 p-6 sm:p-10" data-testid="concept-diagram">
    <div className="mx-auto flex w-fit items-center gap-3 rounded-full border border-slate-200 bg-white px-5 py-2.5 shadow-sm">
      <User className="h-4 w-4 text-upv" />
      <span className="font-display text-sm font-bold tracking-wide">{t("UN PARTICIPANTE")}</span>
      {participant.firstName && <span className="hidden text-xs text-slate-500 sm:inline">· {fullName(participant)}</span>}
    </div>
    <Connector />
    <div className="mx-auto flex w-fit items-center gap-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-md">
      <QRCodeSVG value={qrValue(participant.token)} size={76} fgColor="#0F172A" />
      <div>
        <p className="font-display text-lg font-extrabold">{t("UN QR")}</p>
        <p className="font-mono text-xs font-semibold tracking-wider text-slate-700">{participant.token}</p>
        <p className="mt-1 text-[11px] text-slate-500">{t("Token seguro · sin datos personales")}</p>
      </div>
    </div>
    <Connector />
    <div className="relative grid grid-cols-3 gap-2 sm:gap-4">
      <span className="absolute left-[16.6%] right-[16.6%] top-0 h-px bg-slate-300" />
      {[
        { icon: LogIn, label: "ACCESO" },
        { icon: LogOut, label: "SALIDA" },
        { icon: Building2, label: "EMPRESAS", sub: "Compartir CV" },
      ].map((n) => (
        <div key={n.label} className="flex flex-col items-center">
          <span className="h-5 w-px bg-slate-300" />
          <Node {...n} testId={`concept-node-${n.label.toLowerCase()}`} />
        </div>
      ))}
    </div>
    <Connector />
    <div className="flex items-center justify-center gap-3 rounded-2xl bg-slate-950 px-5 py-4 text-white">
      <Database className="h-4 w-4 text-rose-300" />
      <span className="font-display text-sm font-bold tracking-[0.2em]">UPV DATA PLATFORM</span>
    </div>
  </div>
  );
};

const FLOW = [
  { label: "Registro", icon: UserPlus }, { label: "QR", icon: QrCode }, { label: "Acceso", icon: DoorOpen },
  { label: "Empresas", icon: Building2 }, { label: "CV", icon: FileText }, { label: "Analytics", icon: BarChart3 },
];

export const FlowStrip = () => {
  const { t } = useI18n();
  return (
  <div className="no-scrollbar flex items-center gap-2 overflow-x-auto py-2" data-testid="flow-strip">
    {FLOW.map(({ label, icon: Icon }, i) => (
      <div key={label} className="flex shrink-0 items-center gap-2">
        <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-2.5">
          <span className="font-mono text-[10px] font-bold text-slate-400">0{i + 1}</span>
          <Icon className="h-4 w-4 text-upv" />
          <span className="font-display text-sm font-bold uppercase tracking-wider">{t(label)}</span>
        </div>
        {i < FLOW.length - 1 && <ArrowRight className="h-4 w-4 shrink-0 text-slate-300" />}
      </div>
    ))}
  </div>
  );
};

export const ModuleCard = ({ n, to, icon: Icon, title, desc, live, featured, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`group relative flex min-h-[230px] flex-col justify-between overflow-hidden rounded-[28px] border p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl ${featured ? "border-slate-900 bg-slate-950 text-white" : "border-slate-200 bg-white"}`}
  >
    <div className="flex items-start justify-between">
      <span className={`font-mono text-xs font-bold ${featured ? "text-white/40" : "text-slate-400"}`}>0{n}</span>
      <span className={`flex h-12 w-12 items-center justify-center rounded-2xl transition-colors duration-300 ${featured ? "bg-upv text-white" : "bg-upv-light text-upv group-hover:bg-upv group-hover:text-white"}`}>
        <Icon className="h-5 w-5" />
      </span>
    </div>
    <div>
      <h3 className="font-display text-2xl font-bold tracking-tight">{title}</h3>
      <p className={`mt-2 text-sm leading-relaxed ${featured ? "text-white/60" : "text-slate-500"}`}>{desc}</p>
      <div className="mt-5 flex items-center justify-between">
        <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${featured ? "bg-white/10 text-white/80" : "bg-slate-100 text-slate-600"}`} data-testid={`${testId}-live`}>{live}</span>
        <ArrowRight className={`h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 ${featured ? "text-white" : "text-upv"}`} />
      </div>
    </div>
  </Link>
);

export const MODULE_ICONS = { UserPlus, QrCode, DoorOpen, ScanLine, Building2, BarChart3 };
